// src/utils/request.ts
import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import { AxiosHeaders } from 'axios'
import { ElMessage } from 'element-plus'
import router from '@/router'
import { useAuthStore } from '@/stores/auth'
import { getClientConfig, type ClientConfig, getDeviceFeatures, getNetworkType } from '@/types/client'



// 扩展请求配置类型
interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _isLogoutRequest?: boolean
}

class Request {
  private instance: AxiosInstance
  private clientConfig: ClientConfig

  constructor(config?: Partial<ClientConfig>) {
    this.clientConfig = {
      ...getClientConfig(),
      ...config
    }

    this.instance = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Accept-Language': this.getDefaultLanguage()
      }
    })

    this.setupInterceptors()
  }



  /**
   * 获取屏幕分辨率
   */
  private getScreenResolution(): string {
    const width = window.screen.width * window.devicePixelRatio
    const height = window.screen.height * window.devicePixelRatio
    return `${Math.round(width)}x${Math.round(height)}`
  }

  /**
   * 获取默认语言设置
   */
  private getDefaultLanguage(): string {
    // 优先使用 localStorage 中保存的语言
    const savedLanguage = localStorage.getItem('preferred_language')
    if (savedLanguage) {
      return savedLanguage
    }

    // 使用浏览器语言设置
    if (typeof window !== 'undefined' && navigator) {
      const languages = navigator.languages || [navigator.language || 'zh-CN']
      return languages.join(',')
    }

    // 默认值
    return 'zh-CN,zh;q=0.9,en;q=0.8'
  }

  /**
   * 生成客户端标识头（匹配移动端格式）
   */
  private generateClientHeaders(): Record<string, string> {
    const headers: Record<string, string> = {}

    // 1. 必填的客户端标识头（与移动端保持一致）
    headers['X-Client-Code'] = this.clientConfig.code
    headers['X-Client-Channel'] = this.clientConfig.channel
    headers['X-Client-Type'] = this.clientConfig.type
    headers['X-App-Version'] = this.clientConfig.appVersion
    headers['X-Platform'] = this.clientConfig.platform

    // 2. 设备信息
    // if (this.clientConfig.deviceName) {
    //   headers['X-Device-Name'] = this.clientConfig.deviceName
    // } else {
    //   // 默认设备名
    //   const platform = this.getPlatformInfo()
    //   headers['X-Device-Name'] = `${platform.browser}浏览器设备`
    // }

    // 从 localStorage 获取登录时的设备ID
    const deviceId = localStorage.getItem('deviceId')
    if (deviceId) {
      headers['X-Device-Id'] = deviceId
    }

    // 3. 屏幕和显示信息
    headers['X-Screen-Resolution'] = this.getScreenResolution()

    // 4. 设备特性（Web端特供）
    const deviceFeatures = getDeviceFeatures()
    if (deviceFeatures) {
      headers['X-Device-Features'] = deviceFeatures
    }

    // 5. 网络和环境信息
    const networkType = getNetworkType()
    if (networkType) {
      headers['X-Network-Type'] = networkType
    }

    // 时区（注意移动端是 Asia/shanghai，我们使用标准格式）
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (timezone) {
      headers['X-Timezone'] = timezone.replace(/_/g, '/') // 转为移动端格式
    }

    return headers
  }

  /**
   * 更新设备名称
   */
  public updateDeviceName(name: string): void {
    this.clientConfig.deviceName = name
    // 可以存储到 localStorage
    localStorage.setItem('device_name', name)
  }

  /**
   * 更新用户自定义配置
   */
  public updateClientConfig(config: Partial<ClientConfig>): void {
    this.clientConfig = { ...this.clientConfig, ...config }
  }

  private setupInterceptors() {
    // 请求拦截器
    this.instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {

        const typedConfig = config as CustomAxiosRequestConfig
        // 判断是否为注销请求
        const isLogoutRequest = config.url?.includes('/api/auth/logout')
        typedConfig._isLogoutRequest = isLogoutRequest

        config.headers = config.headers || new AxiosHeaders()



        // 1. 确保基础头部存在
        if (!config.headers['Accept']) {
          config.headers['Accept'] = 'application/json'
        }

        if (!config.headers['Accept-Language']) {
          config.headers['Accept-Language'] = this.getDefaultLanguage()
        }

        // 2. 恢复用户保存的设备名称
        const savedDeviceName = localStorage.getItem('device_name')
        if (savedDeviceName && !this.clientConfig.deviceName) {
          this.clientConfig.deviceName = savedDeviceName
        }

        // 3. 添加客户端标识头（匹配移动端）
        const clientHeaders = this.generateClientHeaders()
        Object.assign(config.headers, clientHeaders)

        // 4. 认证 token
        const authStore = useAuthStore()
        const token = authStore.token;

        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`
        }

        // 5. 开发环境日志
        if (import.meta.env.DEV) {
          const logHeaders = { ...config.headers }
          // 隐藏敏感信息
          if (logHeaders['Authorization']) logHeaders['Authorization'] = 'Bearer ***'
          if (logHeaders['X-Device-Id']) logHeaders['X-Device-Id'] = '***'

          console.group(`📡 API Request: ${config.method?.toUpperCase()} ${config.url}`)
          console.log('📋 Headers:', logHeaders)
          if (config.data) {
            console.log('📦 Request Data:', config.data)
          }
          console.groupEnd()
        }

        return config
      },
      (error) => {
        console.error('🚨 Request Interceptor Error:', error)
        return Promise.reject(error)
      }
    )

    // 响应拦截器（原有逻辑，可保持）
    this.instance.interceptors.response.use(
      (response) => {

        // 重要：如果是下载请求（responseType 为 blob），直接返回完整响应
        if (response.config.responseType === 'blob') {
          return response; // 返回完整响应，不进行 JSON 解析
        }


        if (import.meta.env.DEV) {
          console.log(`✅ API Response: ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`, response.data)
        }

        const { result, state, msg, data } = response.data

        if (result === 0 || state === true) {
          return data
        } else {
          const config = response.config;
          const typedConfig = config as CustomAxiosRequestConfig
          const isLogoutRequest = typedConfig._isLogoutRequest || false

          // 检查是否是 token 过期错误
          const tokenExpiredCodes = [40100, 40101, 40102, 40103, 40105];

          if (tokenExpiredCodes.includes(result)) {
            const authStore = useAuthStore()
            authStore.clearAuth();
            ElMessage.error('登录过期，请重新登录')

            // 跳转到登录页
            if (router.currentRoute.value.path !== '/zhuli') {
              router.push('/zhuli').then(() => {
                console.log("成功跳转到登录页");
              }).catch(err => {
                console.error("跳转失败:", err);
              })
            } else {
              console.log("已经在登录页");
            }

            // 返回一个永远不会 resolve/reject 的 Promise
            // 或者返回一个特殊标记的 Promise，避免触发其他错误处理
            return new Promise(() => { })
          }

          // 其他错误正常处理
          const error = new Error(msg || '请求失败')
            ; (error as any).response = response
            ; (error as any).code = result

          this.handleBusinessError(result, msg)


          return Promise.reject(error)
        }
      },
      (error) => {
        console.error('🚨 API Error:', error)

        // 原有错误处理逻辑...
        return Promise.reject(error)
      }
    )
  }

  /**
   * 处理业务错误码
   */
  /**
   * 处理业务错误码
   */
  private handleBusinessError(code: number, message?: string): void {
    switch (code) {
      case 40100: // 示例：token过期
      case 40101:
      case 40102:
      case 40103:
      case 40105:
        const authStore = useAuthStore()
        authStore.clearAuth();
        // 立即跳转，不等待
        if (router.currentRoute.value.path !== '/zhuli') {
          router.push('/zhuli')
          console.log("跳转")
        } else {
          console.log("已经是了")
        }
        ElMessage.error('登录过期，请重新登录')
        break
      case 40107: // 示例：账号被封禁
      case 40108: // 示例：账号被封禁
        ElMessage.error('账号已被封禁，请联系客服')
        break
      default:
        ElMessage.error(message || '请求失败')
        break;
    }
  }

  // 请求方法保持原有
  public get<T = any>(url: string, config?: InternalAxiosRequestConfig): Promise<T> {
    return this.instance.get(url, config)
  }

  public post<T = any>(url: string, data?: any, config?: InternalAxiosRequestConfig): Promise<T> {
    return this.instance.post(url, data, config)
  }

  public put<T = any>(url: string, data?: any, config?: InternalAxiosRequestConfig): Promise<T> {
    return this.instance.put(url, data, config)
  }

  public delete<T = any>(url: string, config?: InternalAxiosRequestConfig): Promise<T> {
    return this.instance.delete(url, config)
  }

  public patch<T = any>(url: string, data?: any, config?: InternalAxiosRequestConfig): Promise<T> {
    return this.instance.patch(url, data, config)
  }

  /**
   * 下载文件（完全复用现有的拦截器和鉴权头）
   * @param url 下载地址
   * @param data 请求参数
   * @param options 下载选项
   * @returns Promise<boolean> 是否下载成功
   */
  public async download(
    url: string,
    data?: any,
    options?: {
      filename?: string;           // 自定义文件名（优先级低于响应头）
      method?: 'get' | 'post';     // 请求方法，默认 post
      timeout?: number;            // 超时时间，默认 30000
      onProgress?: (progress: number) => void; // 下载进度回调
    }
  ): Promise<boolean> {
    const {
      filename: customFilename,
      method = 'post',
      timeout = 30000,
      onProgress
    } = options || {};

    try {
      // 构建请求配置
      const requestConfig: any = {
        url,
        method,
        responseType: 'blob',
        timeout,
        headers: {
          'Accept': 'application/octet-stream, application/json, */*'
        },
        onDownloadProgress: onProgress ? (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percentCompleted);
          }
        } : undefined
      };

      // 根据请求方法设置数据
      if (method.toLowerCase() === 'get') {
        requestConfig.params = data;
      } else {
        requestConfig.data = data;
      }

      // 发送请求（会经过请求拦截器，自动添加所有鉴权头）
      const response = await this.instance.request(requestConfig);

      // 处理下载响应
      return this.handleDownloadResponse(response, customFilename);
    } catch (error) {
      return this.handleDownloadError(error);
    }
  }

  /**
   * 处理下载响应
   */
  private handleDownloadResponse(response: any, customFilename?: string): boolean {
    // 检查响应是否为 Blob 类型
    if (!(response.data instanceof Blob)) {
      console.error('下载响应不是 Blob 类型');
      ElMessage.error('下载失败：无效的响应数据');
      return false;
    }

    // 检查是否是错误响应（Blob 内容是 JSON）
    if (response.data.type === 'application/json') {
      // 异步处理 JSON 错误，但不阻塞返回
      this.handleJsonResponse(response.data);
      return false;
    }

    // 从响应头获取文件名（优先）
    const filename = this.getFilenameFromResponse(response, customFilename);

    // 触发下载
    this.triggerDownload(response.data, filename);

    // 开发环境日志
    if (import.meta.env.DEV) {
      console.log('✅ Download success:', {
        filename,
        size: this.formatFileSize(response.data.size),
        type: response.data.type
      });
    }

    return true;
  }

  /**
   * 从响应头获取文件名
   */
  private getFilenameFromResponse(response: any, customFilename?: string): string {
    // 1. 优先使用响应头中的文件名
    const contentDisposition = response.headers?.['content-disposition'];
    debugger;
    if (contentDisposition) {
      // 匹配 filename*=UTF-8''%E6%96%87%E4%BB%B6%E5%90%8D.docx 格式 (RFC 5987)
      const utf8Match = contentDisposition.match(/filename\*=(?:UTF-8|utf-8)''([^;]+)/);
      if (utf8Match && utf8Match[1]) {
        try {
          return decodeURIComponent(utf8Match[1]);
        } catch (e) {
          console.warn('UTF-8 文件名解码失败', e);
        }
      }

      // 匹配 filename="文件名.docx" 格式
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        let filename = filenameMatch[1].replace(/['"]/g, '');
        try {
          filename = decodeURIComponent(filename);
        } catch (e) {
          // 如果不是 URL 编码，保留原样
        }
        return filename;
      }
    }

    // 2. 其次使用自定义文件名
    if (customFilename) {
      return customFilename;
    }

    // 3. 从 URL 中提取文件名
    const urlParts = response.config?.url?.split('/') || [];
    const lastPart = urlParts[urlParts.length - 1];
    if (lastPart && lastPart.includes('.')) {
      return lastPart;
    }

    // 4. 生成默认文件名
    const now = new Date();
    const dateStr = `${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}`;
    const timeStr = `${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${now.getSeconds().toString().padStart(2, '0')}`;

    // 尝试从 URL 判断文件类型
    let ext = 'docx'; // 默认
    if (response.config?.url?.includes('.docx')) ext = 'docx';
    else if (response.config?.url?.includes('.pdf')) ext = 'pdf';
    else if (response.config?.url?.includes('.xlsx')) ext = 'xlsx';

    return `download_${dateStr}_${timeStr}.${ext}`;
  }

  /**
   * 格式化文件大小
   */
  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * 处理 JSON 错误响应
   */
  private async handleJsonResponse(blob: Blob): Promise<void> {
    try {
      const text = await blob.text();
      const data = JSON.parse(text);
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL


      // ✅ 先检查是否是下载链接（微信环境）
      if (data.downloadUrl) {
        const fullUrl = new URL(data.downloadUrl, apiBaseUrl).href
        window.location.href = fullUrl
        return;  // 已处理跳转
      }

      const errorData = data;
      const code = errorData.code || errorData.result;
      const message = errorData.msg || errorData.message || '下载失败';

      // 使用现有的业务错误处理
      if (code) {
        this.handleBusinessError(code, message);
      } else {
        ElMessage.error(message);
      }

      if (import.meta.env.DEV) {
        console.error('Download error response:', errorData);
      }
    } catch (e) {
      ElMessage.error('下载失败');
    }
  }

  /**
   * 触发浏览器下载
   */
  private triggerDownload(blob: Blob, filename: string): void {
    // 创建 blob URL
    const url = window.URL.createObjectURL(blob);

    // 创建隐藏的下载链接
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';

    // 添加到文档并触发点击
    document.body.appendChild(link);
    link.click();

    // 清理
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
  }

  /**
   * 处理下载错误
   */
  private async handleDownloadError(error: any): Promise<boolean> {
    console.error('下载失败:', error);

    // 处理网络错误或超时
    if (error.code === 'ECONNABORTED') {
      ElMessage.error('下载超时，请稍后重试');
      return false;
    }

    // 处理 HTTP 错误状态
    if (error.response) {
      const status = error.response.status;

      // 401 未授权
      if (status === 401) {
        const authStore = useAuthStore();
        authStore.clearAuth();
        ElMessage.error('登录过期，请重新登录');

        if (router.currentRoute.value.path !== '/zhuli') {
          router.push('/zhuli');
        }
        return false;
      }

      // 处理错误响应中的 Blob 数据
      if (error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          try {
            const errorData = JSON.parse(text);
            const code = errorData.code || errorData.result;
            const message = errorData.msg || errorData.message || '下载失败';

            if (code) {
              this.handleBusinessError(code, message);
            } else {
              ElMessage.error(message);
            }
          } catch {
            ElMessage.error(text || '下载失败');
          }
        } catch {
          ElMessage.error('下载失败，请稍后重试');
        }
      } else {
        ElMessage.error(`下载失败 (${status})`);
      }
    } else if (error.request) {
      // 请求已发出但没有收到响应
      ElMessage.error('网络异常，请检查网络连接');
    } else {
      // 请求配置出错
      ElMessage.error(error.message || '下载失败');
    }

    return false;
  }
}

const createServerRequestStub = (): Request => {
  const notSupported = () => Promise.reject(new Error('Request is unavailable during server-side rendering'))
  return {
    get: notSupported,
    post: notSupported,
    put: notSupported,
    delete: notSupported,
    patch: notSupported,
    download: async () => false,
    updateDeviceName: () => { },
    updateClientConfig: () => { }
  } as unknown as Request
}

// 创建默认实例
const defaultRequest = typeof window !== 'undefined' ? new Request() : createServerRequestStub()

// 导出
export const request = defaultRequest
export default defaultRequest